// CLUTTER CAPS: clusters below MIN_CLUSTER_ACRES never ring; the rest rank by size and ring
// largest-first until MAX_RINGS or MAX_FIELD_FRACTION of the field is ringed — but the single
// largest cluster always shows, even if it alone exceeds the fraction.
import type { ManifestLayer } from "../types/contract";
import { METRES_PER_DEGREE_LAT, METRES_PER_DEGREE_LNG } from "./metres";

export const MAX_RINGS = 5;
export const MAX_FIELD_FRACTION = 0.15;
export const MIN_CLUSTER_ACRES = 0.5;
/*
 *
 * The raw threshold was a hard |z| ≥ 2 — which admitted small hot patches while a big coherent area
 * at z ≈ 1.7 never qualified at all. Median-smoothing each cell's z over its rook neighbours kills
 * isolated spikes outright (a lone extreme cell's neighbourhood median is ~0) while leaving a
 * coherent region's score — and its exact extent — untouched, which is what justifies the LOWER
 * bar.
 */
export const CLUSTER_Z = 1.5;
const MIN_PAIRS = 30;
const SQM_PER_ACRE = 4046.86;

export interface CompareCluster {
  /* * +1 = the RIGHT/continuous side runs HIGH of the trend here; −1 = low */
  sign: 1 | -1;
  cells: number;
  acres: number;
  /* * the cluster's boundary as bare edge segments — unordered is fine for a dashed line layer */
  lines: GeoJSON.MultiLineString;
  label: { lng: number; lat: number };
  /*
   *
   * WHICH layer the ring names: the one whose values inside the cluster sit further from its OWN
   * field-wide mean, in its own standard deviations. In categorical mode the continuous side is the
   * only candidate.
   */
  attributedSide: "left" | "right";
  /* * that layer's own direction inside the cluster: +1 = it runs HIGH of its own mean, −1 = low */
  attributedSign: 1 | -1;
}

export interface SplitCompareResult {
  mode: "continuous" | "categorical";
  n: number;
  /* * Pearson r (continuous mode only) */
  r: number | null;
  /* * R² (continuous) or eta² (categorical) — "how much of the variation the other layer explains" */
  assoc: number | null;
  /* * OLS residual RMSE in each layer's own units — null for the categorical side */
  typicalLeft: number | null;
  typicalRight: number | null;
  /* * direct RMSE, only when both layers share a unit */
  rmseSame: number | null;
  /* * % of compared cells that are significant outliers (before cluster filtering) */
  outlierPct: number;
  clusters: CompareCluster[];
  dim: GeoJSON.FeatureCollection;
  /* * total acres ringed / total compared acres */
  ringedAcres: number;
  comparedAcres: number;
}

export type CompareFailure = { note: string };

const median = (xs: number[]): number => {
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
};

/* * shoelace area of the first ring, in m², using the local metric projection */
function ringAreaM2(
  geom: { type?: string; coordinates?: unknown },
  kx: number,
  ky: number,
): number {
  const rings =
    (geom.type === "Polygon" ? (geom.coordinates as number[][][]) : null) ?? [];
  const ring = rings[0] ?? [];
  let a = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const [x1, y1] = ring[i]!;
    const [x2, y2] = ring[i + 1]!;
    a += x1! * kx * (y2! * ky) - x2! * kx * (y1! * ky);
  }
  return Math.abs(a) / 2;
}

/*
 *
 * Per-cell values of a picked layer, aligned to the stack cell index. Features skip null cells at
 * build time, so feature order is NOT the cell order — cells are matched by geometry OBJECT identity
 * (cell layers reuse the decoded stack's geom objects) with a first-vertex key as the fallback for
 * layers whose features carry copies.
 */
export function alignLayerToCells(
  layer: ManifestLayer,
  geoms: { type?: string; coordinates?: unknown }[],
  /*
   * minimum matched cells to accept — the default rejects unalignable FOREIGN layers; a caller
   * aligning its OWN assembled layer (the Rx cause chain) passes 1
   */
  minMatched = MIN_PAIRS,
): (number | null)[] | null {
  const fc = layer.inline as GeoJSON.FeatureCollection | undefined;
  if (layer.kind !== "geojson" || !fc?.features?.length) return null;
  const byIdentity = new Map<unknown, number>();
  const byVertex = new Map<string, number>();
  const keyOf = (g: {
    type?: string;
    coordinates?: unknown;
  }): string | null => {
    if (g.type !== "Polygon") return null;
    const v = (g.coordinates as number[][][])[0]?.[0];
    return v ? `${v[0]},${v[1]}` : null;
  };
  geoms.forEach((g, i) => {
    byIdentity.set(g, i);
    const k = keyOf(g);
    if (k) byVertex.set(k, i);
  });
  // RGB layers (bare soil color) carry a `color` string, not a numeric `value` — their comparable
  // scalar is the RELATIVE LUMINANCE (brightness): soil darkness is the classic OM/moisture proxy
  // so "how bright is the bare soil here" is exactly the number worth regressing.
  const luminanceOf = (c: unknown): number | null => {
    if (typeof c !== "string") return null;
    const m =
      /^rgb\((\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?)\)$/.exec(c);
    if (!m) return null;
    return (
      0.2126 * Number(m[1]) + 0.7152 * Number(m[2]) + 0.0722 * Number(m[3])
    );
  };
  const out: (number | null)[] = geoms.map(() => null);
  let matched = 0;
  for (const f of fc.features) {
    const props = f.properties as Record<string, unknown> | null;
    let v = props?.value;
    if (typeof v !== "number" || !Number.isFinite(v))
      v = luminanceOf(props?.color);
    if (typeof v !== "number" || !Number.isFinite(v)) continue;
    const g = f.geometry as { type?: string; coordinates?: unknown };
    let i = byIdentity.get(g);
    if (i == null) {
      const k = keyOf(g);
      i = k != null ? byVertex.get(k) : undefined;
    }
    if (i == null) continue;
    out[i] = v;
    matched++;
  }
  return matched >= minMatched ? out : null;
}

/*
 * Bin arbitrary sampled points (a COG's pixels) onto the cell grid: mean of the points landing
 * nearest each cell centroid, within one cell pitch.
 */
export function binPointsToCells(
  points: { lng: number; lat: number; value: number }[],
  centroids: { lng: number; lat: number }[],
  pitchM: number,
): (number | null)[] {
  if (!centroids.length) return [];
  const lat0 = centroids[0]!.lat;
  const kx = METRES_PER_DEGREE_LNG * Math.cos((lat0 * Math.PI) / 180);
  const ky = METRES_PER_DEGREE_LAT;
  const key = (x: number, y: number) =>
    `${Math.round(x / pitchM)},${Math.round(y / pitchM)}`;
  const buckets = new Map<string, number[]>();
  centroids.forEach((c, i) => {
    const k = key(c.lng * kx, c.lat * ky);
    const b = buckets.get(k);
    if (b) b.push(i);
    else buckets.set(k, [i]);
  });
  const sum = new Float64Array(centroids.length);
  const cnt = new Uint32Array(centroids.length);
  for (const p of points) {
    if (!Number.isFinite(p.value)) continue;
    const x = p.lng * kx;
    const y = p.lat * ky;
    let best = -1;
    let bestD = pitchM * pitchM; // within one pitch
    for (let dx = -1; dx <= 1; dx++)
      for (let dy = -1; dy <= 1; dy++) {
        const b = buckets.get(
          `${Math.round(x / pitchM) + dx},${Math.round(y / pitchM) + dy}`,
        );
        if (!b) continue;
        for (const i of b) {
          const d =
            (centroids[i]!.lng * kx - x) ** 2 +
            (centroids[i]!.lat * ky - y) ** 2;
          if (d < bestD) {
            bestD = d;
            best = i;
          }
        }
      }
    if (best >= 0) {
      sum[best] += p.value;
      cnt[best]++;
    }
  }
  return centroids.map((_, i) => (cnt[i]! > 0 ? sum[i]! / cnt[i]! : null));
}

/*
 * boundary of a set of cells: every polygon edge drawn by exactly ONE member cell (shared edges
 * cancel — the grids share vertices by construction, so keys match exactly)
 */
function clusterBoundary(
  cellIdx: number[],
  geoms: { type?: string; coordinates?: unknown }[],
): GeoJSON.MultiLineString {
  const count = new Map<string, [number[], number[]]>();
  for (const i of cellIdx) {
    const g = geoms[i]!;
    const rings =
      (g.type === "Polygon" ? (g.coordinates as number[][][]) : null) ?? [];
    for (const ring of rings) {
      for (let e = 0; e < ring.length - 1; e++) {
        const a = ring[e]!;
        const b = ring[e + 1]!;
        // keys round to ~1cm: the square grid shares exact vertices, but a legacy hex grid's
        // recomputed corners can drift by float epsilon — without rounding those interior edges
        // would fail to cancel and the ring would render as a full cell mesh
        const q = (v: number) => Math.round(v * 1e7);
        const ka = `${q(a[0]!)},${q(a[1]!)}`;
        const kb = `${q(b[0]!)},${q(b[1]!)}`;
        const k = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
        if (count.has(k))
          count.delete(k); // second sighting cancels (interior edge)
        else count.set(k, [a as number[], b as number[]]);
      }
    }
  }
  return { type: "MultiLineString", coordinates: [...count.values()] };
}

export function compareCellSurfaces(input: {
  leftVals: (number | null)[];
  rightVals: (number | null)[];
  /* * classes rather than magnitudes (the manifest row carries `categories`) */
  leftCategorical: boolean;
  rightCategorical: boolean;
  centroids: { lng: number; lat: number }[];
  geoms: { type?: string; coordinates?: unknown }[];
  sameUnit: boolean;
}): SplitCompareResult | CompareFailure {
  const { leftVals, rightVals, centroids, geoms } = input;
  if (input.leftCategorical && input.rightCategorical) {
    return {
      note: "Both layers are class maps — the compare needs at least one numeric layer.",
    };
  }
  const idx: number[] = [];
  for (let i = 0; i < Math.min(leftVals.length, rightVals.length); i++) {
    const a = leftVals[i];
    const b = rightVals[i];
    if (a != null && Number.isFinite(a) && b != null && Number.isFinite(b))
      idx.push(i);
  }
  if (idx.length < MIN_PAIRS)
    return { note: "Too few overlapping cells to compare these layers." };

  const lat0 = centroids[idx[0]!]!.lat;
  const kx = METRES_PER_DEGREE_LNG * Math.cos((lat0 * Math.PI) / 180);
  const ky = METRES_PER_DEGREE_LAT;
  const cellAreaM2 = ringAreaM2(geoms[idx[0]!]!, kx, ky) || 64;
  const pitchM = Math.sqrt(cellAreaM2);

  const categorical = input.leftCategorical || input.rightCategorical;
  // the CONTINUOUS side plays y; in categorical mode the class side plays the grouping
  const catLeft = input.leftCategorical;
  const xs = idx.map((i) => (catLeft ? rightVals[i]! : leftVals[i]!));
  const ys = idx.map((i) => (catLeft ? leftVals[i]! : rightVals[i]!));
  // continuous mode: x = left, y = right; categorical: xs = continuous values, ys = class codes
  const contVals = categorical ? xs : ys;

  let resid: number[];
  let r: number | null = null;
  let assoc: number | null;
  let typicalLeft: number | null = null;
  let typicalRight: number | null = null;
  // per-compact-position z-scores of each ORIGINAL side vs its own field mean/sd — the cluster
  // attribution's raw material (null in categorical mode, where only the continuous side qualifies)
  let zLeft: number[] | null = null;
  let zRight: number[] | null = null;

  if (!categorical) {
    const n = idx.length;
    const mx = xs.reduce((a, b) => a + b, 0) / n;
    const my = ys.reduce((a, b) => a + b, 0) / n;
    let sxx = 0,
      syy = 0,
      sxy = 0;
    for (let i = 0; i < n; i++) {
      const dx = xs[i]! - mx;
      const dy = ys[i]! - my;
      sxx += dx * dx;
      syy += dy * dy;
      sxy += dx * dy;
    }
    if (sxx === 0 || syy === 0)
      return {
        note: "One of the layers is flat here — nothing to regress against.",
      };
    r = sxy / Math.sqrt(sxx * syy);
    assoc = r * r;
    const s = r >= 0 ? 1 : -1;
    const sdx = Math.sqrt(sxx / n);
    const sdy = Math.sqrt(syy / n);
    // symmetric standardized-major-axis residual: zy − sign(r)·zx
    resid = idx.map((_, i) => (ys[i]! - my) / sdy - s * ((xs[i]! - mx) / sdx));
    // continuous mode: xs = left, ys = right (see the assignment above)
    zLeft = idx.map((_, i) => (xs[i]! - mx) / sdx);
    zRight = idx.map((_, i) => (ys[i]! - my) / sdy);
    // per-layer plain-words deviations: OLS residual RMSE each way
    const bYX = sxy / sxx;
    const bXY = sxy / syy;
    let ssY = 0,
      ssX = 0;
    for (let i = 0; i < n; i++) {
      const ey = ys[i]! - my - bYX * (xs[i]! - mx);
      const ex = xs[i]! - mx - bXY * (ys[i]! - my);
      ssY += ey * ey;
      ssX += ex * ex;
    }
    typicalRight = Math.sqrt(ssY / n);
    typicalLeft = Math.sqrt(ssX / n);
  } else {
    // per-class means of the continuous side; residual = value − its class's mean
    const groups = new Map<number, { sum: number; n: number }>();
    for (let i = 0; i < idx.length; i++) {
      const g = groups.get(ys[i]!) ?? { sum: 0, n: 0 };
      g.sum += contVals[i]!;
      g.n++;
      groups.set(ys[i]!, g);
    }
    const meanOf = new Map([...groups].map(([k, g]) => [k, g.sum / g.n]));
    const grand = contVals.reduce((a, b) => a + b, 0) / contVals.length;
    let ssRes = 0,
      ssTot = 0;
    resid = contVals.map((v, i) => {
      const e = v! - (meanOf.get(ys[i]!) ?? grand);
      ssRes += e * e;
      ssTot += (v! - grand) ** 2;
      return e;
    });
    if (ssTot === 0)
      return {
        note: "The numeric layer is flat here — nothing to compare against the classes.",
      };
    assoc = 1 - ssRes / ssTot;
    const t = Math.sqrt(ssRes / contVals.length);
    if (catLeft) typicalRight = t;
    else typicalLeft = t;
  }

  // robust standardization: median/MAD — the outliers being hunted cannot drag their own yardstick
  const med = median(resid);
  const mad = median(resid.map((d) => Math.abs(d - med))) * 1.4826;
  const outliers: { cell: number; sign: 1 | -1 }[] = [];
  if (mad > 0) {
    const rz = resid.map((d) => (d - med) / mad);
    // NEIGHBOURHOOD MEDIAN (the size calibration): each cell's score is the median z of itself and
    // its rook neighbours (within ~1.2 pitches — diagonals excluded so rings keep exact extents
    // no halo around extreme blocks). A speck's median is ~0 no matter how extreme the speck.
    const allBuckets = new Map<string, number[]>();
    const bAt = (i: number) => {
      const c = centroids[idx[i]!]!;
      return [
        Math.round((c.lng * kx) / pitchM),
        Math.round((c.lat * ky) / pitchM),
      ] as const;
    };
    for (let i = 0; i < idx.length; i++) {
      const [bx, by] = bAt(i);
      const k = `${bx},${by}`;
      const b = allBuckets.get(k);
      if (b) b.push(i);
      else allBuckets.set(k, [i]);
    }
    const nearD2 = (1.2 * pitchM) ** 2;
    for (let i = 0; i < idx.length; i++) {
      const ci = centroids[idx[i]!]!;
      const [bx, by] = bAt(i);
      const hood: number[] = [];
      for (let dx = -2; dx <= 2; dx++)
        for (let dy = -2; dy <= 2; dy++) {
          for (const j of allBuckets.get(`${bx + dx},${by + dy}`) ?? []) {
            const cj = centroids[idx[j]!]!;
            const d2 =
              ((cj.lng - ci.lng) * kx) ** 2 + ((cj.lat - ci.lat) * ky) ** 2;
            if (d2 <= nearD2) hood.push(rz[j]!);
          }
        }
      const sz = median(hood);
      if (Math.abs(sz) >= CLUSTER_Z)
        outliers.push({ cell: idx[i]!, sign: sz > 0 ? 1 : -1 });
    }
  }

  // spatial clustering: same-sign outlier cells within 1.5 pitches link (rook+diagonal on the grid)
  const signOf = new Map(outliers.map((o) => [o.cell, o.sign]));
  const bkey = (i: number) => {
    const c = centroids[i]!;
    return `${Math.round((c.lng * kx) / pitchM)},${Math.round((c.lat * ky) / pitchM)}`;
  };
  const buckets = new Map<string, number[]>();
  for (const o of outliers) {
    const k = bkey(o.cell);
    const b = buckets.get(k);
    if (b) b.push(o.cell);
    else buckets.set(k, [o.cell]);
  }
  const linkD2 = (1.6 * pitchM) ** 2;
  const seen = new Set<number>();
  const rawClusters: { cells: number[]; sign: 1 | -1 }[] = [];
  for (const o of outliers) {
    if (seen.has(o.cell)) continue;
    const queue = [o.cell];
    seen.add(o.cell);
    const cells: number[] = [];
    while (queue.length) {
      const cur = queue.pop()!;
      cells.push(cur);
      const cc = centroids[cur]!;
      const cx = Math.round((cc.lng * kx) / pitchM);
      const cy = Math.round((cc.lat * ky) / pitchM);
      for (let dx = -2; dx <= 2; dx++)
        for (let dy = -2; dy <= 2; dy++) {
          for (const nb of buckets.get(`${cx + dx},${cy + dy}`) ?? []) {
            if (seen.has(nb) || signOf.get(nb) !== o.sign) continue;
            const nc = centroids[nb]!;
            const d2 =
              ((nc.lng - cc.lng) * kx) ** 2 + ((nc.lat - cc.lat) * ky) ** 2;
            if (d2 <= linkD2) {
              seen.add(nb);
              queue.push(nb);
            }
          }
        }
    }
    rawClusters.push({ cells, sign: o.sign });
  }

  // FOCUS + CLUTTER CAP: drop sub-half-acre specks, rank the rest by size, ring largest-first until
  // MAX_RINGS or MAX_FIELD_FRACTION of the compared area — the single largest always rings.
  const minCells = Math.max(
    3,
    Math.ceil((MIN_CLUSTER_ACRES * SQM_PER_ACRE) / cellAreaM2),
  );
  const posOf = new Map(idx.map((cell, i) => [cell, i]));
  const contSide: "left" | "right" = catLeft ? "right" : "left"; // categorical mode's only nameable side
  const ranked = rawClusters
    .filter((c) => c.cells.length >= minCells)
    .sort((a, b) => b.cells.length - a.cells.length);
  const maxCells = MAX_FIELD_FRACTION * idx.length;
  const clusters: CompareCluster[] = [];
  const bright = new Set<number>();
  let cum = 0;
  for (const c of ranked) {
    if (clusters.length >= MAX_RINGS) break;
    if (clusters.length > 0 && cum + c.cells.length > maxCells) break;
    cum += c.cells.length;
    for (const cell of c.cells) bright.add(cell);
    const lng =
      c.cells.reduce((a, i) => a + centroids[i]!.lng, 0) / c.cells.length;
    const lat =
      c.cells.reduce((a, i) => a + centroids[i]!.lat, 0) / c.cells.length;
    // ATTRIBUTION: name the layer whose cluster values sit furthest from its OWN overall mean, in
    // its own standard deviations, with THAT layer's direction.
    let attributedSide: "left" | "right";
    let attributedSign: 1 | -1;
    if (zLeft && zRight) {
      let sl = 0,
        sr = 0;
      for (const cell of c.cells) {
        const p = posOf.get(cell)!;
        sl += zLeft[p]!;
        sr += zRight[p]!;
      }
      const mzl = sl / c.cells.length;
      const mzr = sr / c.cells.length;
      attributedSide = Math.abs(mzl) > Math.abs(mzr) ? "left" : "right";
      const z = attributedSide === "left" ? mzl : mzr;
      attributedSign = z >= 0 ? 1 : -1;
    } else {
      // categorical: only the continuous side can be named; its class-relative direction stands
      attributedSide = contSide;
      attributedSign = c.sign;
    }
    clusters.push({
      sign: c.sign,
      cells: c.cells.length,
      acres: (c.cells.length * cellAreaM2) / SQM_PER_ACRE,
      lines: clusterBoundary(c.cells, geoms),
      label: { lng, lat },
      attributedSide,
      attributedSign,
    });
  }

  // same-unit direct RMSE (the one place a raw RMSE means something)
  let rmseSame: number | null = null;
  if (input.sameUnit && !categorical) {
    let ss = 0;
    for (let i = 0; i < idx.length; i++) ss += (ys[i]! - xs[i]!) ** 2;
    rmseSame = Math.sqrt(ss / idx.length);
  }

  // bright = ring interiors exactly, matching the Rx cause compare's dim discipline: everything
  // compared but not ringed darkens — including outlier patches the clutter caps dropped
  const dim: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: clusters.length
      ? idx.flatMap((i) =>
          bright.has(i)
            ? []
            : [
                {
                  type: "Feature" as const,
                  properties: {},
                  geometry: geoms[i] as GeoJSON.Geometry,
                },
              ],
        )
      : [],
  };

  return {
    mode: categorical ? "categorical" : "continuous",
    n: idx.length,
    dim,
    r,
    assoc,
    typicalLeft,
    typicalRight,
    rmseSame,
    outlierPct: (outliers.length / idx.length) * 100,
    clusters,
    ringedAcres: (cum * cellAreaM2) / SQM_PER_ACRE,
    comparedAcres: (idx.length * cellAreaM2) / SQM_PER_ACRE,
  };
}

export function isCompareFailure(
  x: SplitCompareResult | CompareFailure,
): x is CompareFailure {
  return (x as CompareFailure).note !== undefined;
}

// Label 0 (and null) never ring: "similar" is the background, not a finding.
export interface LabeledCluster {
  cat: number;
  /* * the mean per-cell delta's sign inside the cluster: +1 = the RIGHT/genius side runs higher */
  sign: 1 | -1;
  cells: number;
  cellIdx: number[];
  acres: number;
  lines: GeoJSON.MultiLineString;
  label: { lng: number; lat: number };
}

export function clusterLabeledCells(
  cats: (number | null)[],
  deltas: (number | null)[],
  centroids: { lng: number; lat: number }[],
  geoms: { type?: string; coordinates?: unknown }[],
  /*
   * false = return EVERY above-floor cluster ranked by size; the caller caps the FILTERED set
   * (the Rx cause picker rings one cause at a time — capping across causes first would starve
   * whichever cause the user then picks)
   */
  applyCaps = true,
): LabeledCluster[] {
  const idx: number[] = [];
  for (let i = 0; i < cats.length; i++)
    if (cats[i] != null && cats[i] !== 0) idx.push(i);
  if (!idx.length) return [];
  const lat0 = centroids[idx[0]!]!.lat;
  const kx = METRES_PER_DEGREE_LNG * Math.cos((lat0 * Math.PI) / 180);
  const ky = METRES_PER_DEGREE_LAT;
  const cellAreaM2 = cellPitchOf(geoms, centroids) ** 2 || 64;
  const pitchM = Math.sqrt(cellAreaM2);
  const bkey = (i: number) => {
    const c = centroids[i]!;
    return `${Math.round((c.lng * kx) / pitchM)},${Math.round((c.lat * ky) / pitchM)}`;
  };
  const buckets = new Map<string, number[]>();
  for (const i of idx) {
    const k = bkey(i);
    const b = buckets.get(k);
    if (b) b.push(i);
    else buckets.set(k, [i]);
  }
  const linkD2 = (1.6 * pitchM) ** 2;
  const seen = new Set<number>();
  const raw: { cat: number; cells: number[] }[] = [];
  for (const start of idx) {
    if (seen.has(start)) continue;
    const cat = cats[start]!;
    const queue = [start];
    seen.add(start);
    const cells: number[] = [];
    while (queue.length) {
      const cur = queue.pop()!;
      cells.push(cur);
      const cc = centroids[cur]!;
      const cx = Math.round((cc.lng * kx) / pitchM);
      const cy = Math.round((cc.lat * ky) / pitchM);
      for (let dx = -2; dx <= 2; dx++)
        for (let dy = -2; dy <= 2; dy++) {
          for (const nb of buckets.get(`${cx + dx},${cy + dy}`) ?? []) {
            if (seen.has(nb) || cats[nb] !== cat) continue;
            const nc = centroids[nb]!;
            const d2 =
              ((nc.lng - cc.lng) * kx) ** 2 + ((nc.lat - cc.lat) * ky) ** 2;
            if (d2 <= linkD2) {
              seen.add(nb);
              queue.push(nb);
            }
          }
        }
    }
    raw.push({ cat, cells });
  }
  // the SAME clutter caps as the outlier rings: half-acre floor, largest-first, ring count + field
  // fraction bounded, the single largest always shows
  const minCells = Math.max(
    3,
    Math.ceil((MIN_CLUSTER_ACRES * SQM_PER_ACRE) / cellAreaM2),
  );
  const compared =
    deltas.filter((d) => d != null && Number.isFinite(d)).length || cats.length;
  const maxCells = MAX_FIELD_FRACTION * compared;
  const ranked = raw
    .filter((c) => c.cells.length >= minCells)
    .sort((a, b) => b.cells.length - a.cells.length);
  const out: LabeledCluster[] = [];
  let cum = 0;
  for (const c of ranked) {
    if (applyCaps) {
      if (out.length >= MAX_RINGS) break;
      if (out.length > 0 && cum + c.cells.length > maxCells) break;
    }
    cum += c.cells.length;
    let dsum = 0;
    for (const i of c.cells) dsum += deltas[i] ?? 0;
    const lng =
      c.cells.reduce((a, i) => a + centroids[i]!.lng, 0) / c.cells.length;
    const lat =
      c.cells.reduce((a, i) => a + centroids[i]!.lat, 0) / c.cells.length;
    out.push({
      cat: c.cat,
      sign: dsum >= 0 ? 1 : -1,
      cells: c.cells.length,
      cellIdx: c.cells,
      acres: (c.cells.length * cellAreaM2) / SQM_PER_ACRE,
      lines: clusterBoundary(c.cells, geoms),
      label: { lng, lat },
    });
  }
  return out;
}

/*
 *
 * MAJORITY-FILTER DESPECKLE — "I don't want to see small patches of only a handfu of pixels
 * selected/deselected in the middle of a larger cluster").
 */
export function smoothCategories(
  cats: (number | null)[],
  centroids: { lng: number; lat: number }[],
  geoms: { type?: string; coordinates?: unknown }[],
  passes = 2,
): (number | null)[] {
  const idx: number[] = [];
  for (let i = 0; i < cats.length; i++) if (cats[i] != null) idx.push(i);
  if (!idx.length) return cats.slice();
  const lat0 = centroids[idx[0]!]!.lat;
  const kx = METRES_PER_DEGREE_LNG * Math.cos((lat0 * Math.PI) / 180);
  const ky = METRES_PER_DEGREE_LAT;
  const pitchM = cellPitchOf(geoms, centroids);
  const bkey = (i: number) => {
    const c = centroids[i]!;
    return `${Math.round((c.lng * kx) / pitchM)},${Math.round((c.lat * ky) / pitchM)}`;
  };
  const buckets = new Map<string, number[]>();
  for (const i of idx) {
    const k = bkey(i);
    const b = buckets.get(k);
    if (b) b.push(i);
    else buckets.set(k, [i]);
  }
  const nearD2 = (1.6 * pitchM) ** 2;
  let cur = cats.slice();
  for (let pass = 0; pass < passes; pass++) {
    const next = cur.slice();
    for (const i of idx) {
      const ci = centroids[i]!;
      const cx = Math.round((ci.lng * kx) / pitchM);
      const cy = Math.round((ci.lat * ky) / pitchM);
      const counts = new Map<number, number>();
      for (let dx = -2; dx <= 2; dx++)
        for (let dy = -2; dy <= 2; dy++) {
          for (const j of buckets.get(`${cx + dx},${cy + dy}`) ?? []) {
            const cj = centroids[j]!;
            const d2 =
              ((cj.lng - ci.lng) * kx) ** 2 + ((cj.lat - ci.lat) * ky) ** 2;
            if (d2 > nearD2) continue;
            const c = cur[j];
            if (c == null) continue;
            counts.set(c, (counts.get(c) ?? 0) + 1);
          }
        }
      const own = cur[i]!;
      let best = own;
      let bestN = counts.get(own) ?? 0;
      for (const [c, n] of counts)
        if (n > bestN) {
          best = c;
          bestN = n;
        }
      next[i] = best;
    }
    cur = next;
  }
  return cur;
}

/*
 * the ring clutter caps, applied to an already-FILTERED, size-ranked cluster list (the largest
 * always shows; then largest-first until MAX_RINGS or MAX_FIELD_FRACTION of the compared cells)
 */
export function capClusters(
  clusters: LabeledCluster[],
  comparedCells: number,
): LabeledCluster[] {
  const maxCells = MAX_FIELD_FRACTION * Math.max(1, comparedCells);
  const out: LabeledCluster[] = [];
  let cum = 0;
  for (const c of [...clusters].sort((a, b) => b.cells - a.cells)) {
    if (out.length >= MAX_RINGS) break;
    if (out.length > 0 && cum + c.cells > maxCells) break;
    cum += c.cells;
    out.push(c);
  }
  return out;
}

/* * the grid's cell pitch in metres, from the first drawable cell — the binning radius for COG pixels */
export function cellPitchOf(
  geoms: { type?: string; coordinates?: unknown }[],
  centroids: { lng: number; lat: number }[],
): number {
  const lat0 = centroids[0]?.lat ?? 40;
  const kx = METRES_PER_DEGREE_LNG * Math.cos((lat0 * Math.PI) / 180);
  const g = geoms.find((gg) => gg?.type === "Polygon");
  return g ? Math.sqrt(ringAreaM2(g, kx, METRES_PER_DEGREE_LAT) || 64) : 8;
}
